import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Clock, Euro, Send, CheckCircle, Upload, Trash2, AlertTriangle, AlertCircle, XCircle, Inbox, HelpCircle, MessageCircle, Building2 } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { isAuthenticated, getAuthHeader, getAssociateData } from '../services/otpAuthService';
import DocumentiVisualizzazione from '../components/svincolo-atti/DocumentiVisualizzazione';
import { useTour } from '../hooks/useTour';
import GuidedTour from '../components/GuidedTour';
import { useSvincoloAttiSocket } from '../hooks/useSvincoloAttiSocket';

interface AttoSvincolo {
  _id: string;
  targa: string;
  tipoAtto: string;
  prezzoVendita: number;
  soggettoIVA: boolean;
  dataAppuntamento: string;
  oraAppuntamento: string;
  modalitaPagamento: string;
  emailVenditore: string;
  status: string;
  dataInvio: string;
  studiSelezionati: any[];
  preventivi: any[];
  rifiuti?: any[];
  richiedente: any;
  studioAssegnato?: any;
  sediSelezionate?: any[];
  sedeAssegnata?: string | null;
  noteRichiedente?: string;
  documenti?: any[];
  ibanStudioSelezionato?: string;
  ricevutaBonifico?: {
    filename: string;
    originalName: string;
    path: string;
    uploadDate: string;
  };
  pagamentoConfermato?: boolean;
  dataCompletamento?: string;
  attoVenditaPDF?: {
    filename: string;
    originalName: string;
    path: string;
    uploadDate: string;
  };
  numeroCartellaPRA?: string;
}

const SvincoloAttiDettaglioPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [atto, setAtto] = useState<AttoSvincolo | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [motivoCancellazione, setMotivoCancellazione] = useState('');
  const [showRifiutoForm, setShowRifiutoForm] = useState(false);
  const [preventivo, setPreventivo] = useState({
    importo: '',
    note: ''
  });
  const [motivazioneRifiuto, setMotivazioneRifiuto] = useState('');
  const [iban, setIban] = useState('');
  const [ricevutaBonifico, setRicevutaBonifico] = useState<File | null>(null);
  const [attoVenditaPDF, setAttoVenditaPDF] = useState<File | null>(null);
  const [numeroCartellaPRA, setNumeroCartellaPRA] = useState('');

  // Chat
  const [messaggi, setMessaggi] = useState<any[]>([]);
  const [nuovoMessaggio, setNuovoMessaggio] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Guided tour
  const { runTour, tourKey, startTour, handleCallback } = useTour('svincolo_dettaglio_tour');

  const tourSteps = [
    {
      target: '#tour-dettaglio-status',
      content: 'Qui vedi lo stato attuale della richiesta.',
      skipBeacon: true,
      placement: 'bottom' as const
    },
    {
      target: '#tour-dettaglio-dati',
      content: 'Questi sono i dati del veicolo e dell\'atto di svincolo.',
      placement: 'bottom' as const
    },
    ...(document.getElementById('preventivi') ? [{
      target: '#preventivi',
      content: 'Qui trovi i preventivi ricevuti dagli studi. Puoi accettarne uno per procedere.',
      placement: 'top' as const
    }] : []),
    ...(document.getElementById('pagamento') ? [{
      target: '#pagamento',
      content: 'Una volta accettato un preventivo, qui gestisci il pagamento.',
      placement: 'top' as const
    }] : []),
    ...(document.getElementById('gestione') ? [{
      target: '#gestione',
      content: 'Da qui puoi caricare documenti, inserire l\'IBAN e confermare il completamento.',
      placement: 'top' as const
    }] : [])
  ];

  const API_BASE_URL = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/graphql', '')
    : 'http://localhost:5000';

  const associateData = getAssociateData();

  // Scroll to top quando la pagina viene caricata o quando cambia l'atto
  useEffect(() => {
    // Se c'è un hash nell'URL, scrolla alla sezione specifica
    const hash = window.location.hash.substring(1); // Rimuove il #
    if (hash) {
      // Aspetta che il DOM sia renderizzato
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated() && !token) {
      toast.error('Devi effettuare l\'accesso');
      navigate('/accesso-associati');
      return;
    }

    loadDettaglio();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [id, token, navigate]);

  const loadDettaglio = async () => {
    try {
      setLoading(true);

      const url = token
        ? `${API_BASE_URL}/api/atti-svincolo/${id}?token=${token}`
        : `${API_BASE_URL}/api/atti-svincolo/${id}`;

      const config = token ? {} : { headers: getAuthHeader() };

      const response = await axios.get(url, config);
      setAtto(response.data.data);
    } catch (error: any) {
      console.error('Errore caricamento dettaglio:', error);
      toast.error('Errore durante il caricamento del dettaglio');
      navigate('/area-riservata/svincolo-atti/lista');
    } finally {
      setLoading(false);
    }
  };

  const handleInviaPreventivo = async () => {
    if (!preventivo.importo) {
      toast.error('Inserisci l\'importo del preventivo');
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/atti-svincolo/${id}/preventivo`,
        {
          importo: parseFloat(preventivo.importo),
          note: preventivo.note
        },
        { headers: getAuthHeader() }
      );

      // Mostra messaggio di successo con info
      toast.success(
        'Preventivo inviato con successo! Se sarà accettato riceverai un messaggio via email.',
        { duration: 5000 }
      );

      // Redirect alla lista richieste ricevute dopo 2 secondi
      setTimeout(() => {
        navigate('/area-riservata/svincolo-atti/lista');
      }, 2000);
    } catch (error: any) {
      console.error('Errore invio preventivo:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'invio del preventivo');
    }
  };

  const handleAccettaPreventivo = async (preventivoId: string) => {
    if (!window.confirm('Sei sicuro di voler accettare questo preventivo?')) {
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/atti-svincolo/${id}/accetta-preventivo`,
        { preventivoId },
        { headers: getAuthHeader() }
      );

      toast.success('Preventivo accettato! Lo studio è stato notificato.');
      loadDettaglio();
    } catch (error: any) {
      console.error('Errore accettazione preventivo:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'accettazione del preventivo');
    }
  };

  const handleCancellaRichiesta = async () => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/atti-svincolo/${id}`,
        {
          headers: getAuthHeader(),
          data: { motivoCancellazione: motivoCancellazione.trim() || undefined }
        }
      );

      toast.success('Richiesta cancellata con successo. Gli studi selezionati sono stati notificati.');
      setShowCancelModal(false);
      setMotivoCancellazione('');

      setTimeout(() => {
        navigate('/area-riservata/svincolo-atti/lista');
      }, 2000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore durante la cancellazione della richiesta');
      setShowCancelModal(false);
    }
  };

  const handleRifiutaRichiesta = async () => {
    if (!motivazioneRifiuto || motivazioneRifiuto.trim() === '') {
      toast.error('Inserisci la motivazione del rifiuto');
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/atti-svincolo/${id}/rifiuta`,
        { motivazione: motivazioneRifiuto },
        { headers: getAuthHeader() }
      );

      toast.success('Rifiuto registrato. Il richiedente è stato notificato.');
      setShowRifiutoForm(false);
      setMotivazioneRifiuto('');

      // Redirect alla lista dopo 2 secondi
      setTimeout(() => {
        navigate('/area-riservata/svincolo-atti/lista');
      }, 2000);
    } catch (error: any) {
      console.error('Errore rifiuto richiesta:', error);
      toast.error(error.response?.data?.message || 'Errore durante il rifiuto della richiesta');
    }
  };

  const handleInserisciIban = async () => {
    if (!iban || iban.trim() === '') {
      toast.error('Inserisci il tuo IBAN');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/atti-svincolo/${id}/inserisci-iban`,
        { iban: iban.trim() },
        { headers: getAuthHeader() }
      );

      toast.success('IBAN salvato! Il richiedente è stato notificato.');
      setAtto(response.data.data);
      setIban('');
    } catch (error: any) {
      console.error('Errore inserimento IBAN:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'inserimento dell\'IBAN');
    }
  };

  const handleCaricaRicevuta = async () => {
    if (!ricevutaBonifico) {
      toast.error('Seleziona la ricevuta del bonifico');
      return;
    }

    const formData = new FormData();
    formData.append('ricevuta', ricevutaBonifico);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/atti-svincolo/${id}/upload-ricevuta-bonifico`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Ricevuta caricata! Lo studio è stato notificato.');
      setAtto(response.data.data);
      setRicevutaBonifico(null);
    } catch (error: any) {
      console.error('Errore caricamento ricevuta:', error);
      toast.error(error.response?.data?.message || 'Errore durante il caricamento della ricevuta');
    }
  };

  const handleConfermaCompletamento = async () => {
    if (!attoVenditaPDF) {
      toast.error('Carica l\'atto di vendita in PDF');
      return;
    }

    if (!numeroCartellaPRA || numeroCartellaPRA.trim() === '') {
      toast.error('Inserisci il numero di cartella PRA');
      return;
    }

    if (!window.confirm('Confermi di aver completato la pratica e ricevuto il pagamento?')) {
      return;
    }

    const formData = new FormData();
    formData.append('attoVendita', attoVenditaPDF);
    formData.append('numeroCartellaPRA', numeroCartellaPRA.trim());

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/atti-svincolo/${id}/conferma-completamento`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('☑️ Pratica completata con successo!');
      setAtto(response.data.data);
      setAttoVenditaPDF(null);
      setNumeroCartellaPRA('');
    } catch (error: any) {
      console.error('Errore conferma completamento:', error);
      toast.error(error.response?.data?.message || 'Errore durante la conferma del completamento');
    }
  };

  const getTipoAttoLabel = (tipoAtto: string) => {
    const labels: Record<string, string> = {
      'articolo56': 'Art. 56',
      'miniPassaggio': 'Mini Passaggio',
      'passaggioRegolare': 'Passaggio Regolare'
    };
    return labels[tipoAtto] || tipoAtto;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Funzione per ottenere il badge di stato
  const getStatusBadge = () => {
    if (!atto) return null;

    const isStudioAssegnatoCheck = atto.studioAssegnato?.associateId?._id === associateData?.id;
    const isStudioSelezionatoCheck = atto.studiSelezionati?.some(
      (studio: any) => studio.associateId?._id === associateData?.id
    );
    const isRichiedenteCheck = atto.richiedente?._id === associateData?.id;

    // Per studio selezionato ma non assegnato
    if (isStudioSelezionatoCheck && atto.status === 'assegnata' && !isStudioAssegnatoCheck) {
      return (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
          <XCircle className="w-4 h-4" />
          Non Selezionato
        </span>
      );
    }

    // Per studio assegnato
    if (isStudioAssegnatoCheck && atto.status === 'assegnata') {
      return (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-300 border-2 border-green-500 dark:border-green-600">
          <CheckCircle className="w-4 h-4" />
          ☑️ Sei Stato Selezionato
        </span>
      );
    }

    // Per studio selezionato in attesa di preventivo
    if (isStudioSelezionatoCheck && (atto.status === 'inviata' || atto.status === 'in_attesa_preventivi')) {
      return (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 dark:from-orange-900/30 dark:to-red-900/30 dark:text-orange-300 border-2 border-orange-500 dark:border-orange-600">
          <AlertCircle className="w-4 h-4" />
          Richiede Azione - Invia Preventivo
        </span>
      );
    }

    // Per richiedente con preventivi ricevuti
    if (isRichiedenteCheck && atto.status === 'preventivi_ricevuti') {
      return (
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 dark:from-purple-900/30 dark:to-pink-900/30 dark:text-purple-300 border-2 border-purple-500 dark:border-purple-600">
          <AlertCircle className="w-4 h-4" />
          Richiede Azione - Valuta Preventivi
        </span>
      );
    }

    // Stati normali
    const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
      'inviata': { label: 'Inviata', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-300 dark:border-blue-600', icon: Send },
      'in_attesa_preventivi': { label: 'In attesa preventivi', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-600', icon: Clock },
      'preventivi_ricevuti': { label: 'Preventivi ricevuti', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-300 dark:border-purple-600', icon: Inbox },
      'assegnata': { label: 'Assegnata', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-300 dark:border-green-600', icon: CheckCircle },
      'documenti_caricati': { label: 'Documenti Caricati', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-600', icon: CheckCircle },
      'in_attesa_pagamento': { label: 'In Attesa Pagamento', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 dark:border-amber-600', icon: Euro },
      'completata': { label: 'Completata', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300 border border-gray-300 dark:border-gray-600', icon: CheckCircle },
      'annullata': { label: 'Annullata', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border border-red-300 dark:border-red-600', icon: XCircle },
    };

    const config = statusConfig[atto.status] || statusConfig['inviata'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  // Verifica se l'utente loggato è il richiedente
  const isRichiedente = atto?.richiedente?._id === associateData?.id;

  // Verifica se l'utente loggato è uno degli studi selezionati
  const isStudioSelezionato = atto?.studiSelezionati?.some(
    (studio: any) => studio.associateId?._id === associateData?.id
  );

  // Verifica se l'utente loggato è lo studio assegnato
  const isStudioAssegnato = atto?.studioAssegnato?.associateId?._id === associateData?.id;

  // Verifica se l'utente ha già inviato un preventivo
  const mioPreventivoEsistente = atto?.preventivi?.find(
    (p: any) => p.studio?.associateId?._id === associateData?.id
  );

  // Chat: visibilita e permessi
  const canViewChat = isRichiedente || isStudioAssegnato || (isStudioSelezionato && !atto?.studioAssegnato);
  const isChatOpen = atto ? (
    !['annullata', 'scaduta'].includes(atto.status) &&
    (atto.status !== 'completata' || (atto.dataCompletamento && (Date.now() - new Date(atto.dataCompletamento).getTime() < 48 * 60 * 60 * 1000)))
  ) : false;
  const canWrite = isChatOpen && (
    isRichiedente || (isStudioAssegnato && !!atto?.studioAssegnato)
  );

  const handleNewMessage = useCallback((msg: any) => {
    setMessaggi(prev => [...prev, {
      _id: msg.id,
      mittente: msg.mittente,
      testo: msg.testo,
      tipo: msg.tipo,
      fase: msg.fase,
      createdAt: msg.createdAt
    }]);
  }, []);

  const {
    connected: socketConnected,
    onlineUsers,
    typingUsers,
    sendMessage: sendSocketMessage,
    startTyping: handleTyping,
    markAsRead
  } = useSvincoloAttiSocket({
    attoId: id || '',
    onNewMessage: handleNewMessage,
    enabled: !!(canViewChat && id)
  });

  const loadMessaggi = async () => {
    if (!id || !canViewChat) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/atti-svincolo/${id}/messaggi`,
        { headers: getAuthHeader() }
      );
      setMessaggi(response.data.data || []);
    } catch (error: any) {
      // Silently fail
    }
  };

  useEffect(() => {
    if (atto && canViewChat) {
      loadMessaggi();
      if (socketConnected) markAsRead();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [atto?._id, socketConnected, canViewChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messaggi]);

  const handleInviaMessaggio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nuovoMessaggio.trim()) return;

    if (socketConnected) {
      const success = sendSocketMessage(nuovoMessaggio.trim());
      if (success) setNuovoMessaggio('');
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/atti-svincolo/${id}/messaggi`,
        { testo: nuovoMessaggio.trim() },
        { headers: getAuthHeader() }
      );
      setNuovoMessaggio('');
      loadMessaggi();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore invio messaggio');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento dettagli...</p>
        </div>
      </div>
    );
  }

  if (!atto) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <GuidedTour steps={tourSteps} run={runTour} tourKey={tourKey} onCallback={handleCallback} />
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/area-riservata/svincolo-atti/lista')}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-base font-bold text-gray-900 dark:text-white">
                  Targa {atto.targa}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">{getTipoAttoLabel(atto.tipoAtto)}</span>
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={startTour}
                className="p-2 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors"
                title="Guida"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
              {/* Pulsante Cancella (solo per richiedente e solo se non assegnata) */}
              {isRichiedente && !atto.studioAssegnato && atto.status !== 'annullata' && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Cancella Richiesta
                </button>
              )}
            </div>
          </div>

          {/* Badge di Stato */}
          <div id="tour-dettaglio-status" className="flex items-center gap-3">
            {getStatusBadge()}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Colonna Principale */}
          <div className="lg:col-span-2 space-y-3">
            {/* Dati Veicolo */}
            <div id="tour-dettaglio-dati" className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2.5 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                Dati Veicolo e Atto
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Targa</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{atto.targa}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Tipo Atto</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{getTipoAttoLabel(atto.tipoAtto)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Prezzo Vendita</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(atto.prezzoVendita)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Soggetto IVA</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{atto.soggettoIVA ? 'Sì' : 'No'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Pagamento</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{atto.modalitaPagamento}</p>
                </div>
              </div>
            </div>

            {/* Appuntamento */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2.5 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                Appuntamento
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Data e ora</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{formatDate(atto.dataAppuntamento)} alle {atto.oraAppuntamento}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Email Venditore</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{atto.emailVenditore}</p>
                </div>
              </div>
            </div>

            {/* Chat (le note del richiedente appaiono come primo messaggio) */}
            {canViewChat && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-l-cyan-500 dark:border-l-cyan-400 border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2.5">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                    Messaggi
                    {messaggi.length > 0 && (
                      <span className="text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 px-1.5 py-0.5 rounded-full">{messaggi.length}</span>
                    )}
                  </h2>
                  {socketConnected && onlineUsers.filter(u => !u.isAdmin).length > 0 && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {onlineUsers.filter(u => !u.isAdmin).length} online
                    </span>
                  )}
                </div>

                {isStudioSelezionato && !isRichiedente && !atto?.studioAssegnato && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded px-2.5 py-1.5 mb-2">
                    Solo lettura — solo il richiedente puo scrivere prima dell'assegnazione.
                  </p>
                )}

                {!isChatOpen && messaggi.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded px-2.5 py-1.5 mb-2">
                    Messaggistica chiusa.
                  </p>
                )}

                <div className="space-y-2 max-h-96 overflow-y-auto mb-2">
                  {messaggi.length === 0 ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">
                      Nessun messaggio.{canWrite && ' Inizia la conversazione!'}
                    </p>
                  ) : (
                    messaggi.map((msg: any) => {
                      const isMine = msg.mittente?._id === associateData?.id;
                      const isSistema = msg.tipo === 'sistema';

                      if (isSistema) {
                        return (
                          <div key={msg._id} className="flex justify-center">
                            <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                              {msg.testo}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div key={msg._id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                            isMine
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          }`}>
                            {!isMine && (
                              <p className={`text-xs font-medium mb-0.5 ${isMine ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'}`}>
                                {msg.mittente?.nominativo || 'Sconosciuto'}
                              </p>
                            )}
                            <p className="text-sm">{msg.testo}</p>
                            <p className={`text-xs mt-0.5 ${isMine ? 'text-blue-200' : 'text-gray-400 dark:text-gray-500'}`}>
                              {new Date(msg.createdAt).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {typingUsers.length > 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic mb-1">
                    {typingUsers.map(t => t.name).join(', ')} sta scrivendo...
                  </p>
                )}

                {canWrite && (
                  <form onSubmit={handleInviaMessaggio} className="flex gap-2">
                    <input
                      type="text"
                      value={nuovoMessaggio}
                      onChange={(e) => { setNuovoMessaggio(e.target.value); handleTyping(); }}
                      placeholder="Scrivi un messaggio..."
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 dark:bg-gray-700 dark:text-white"
                      disabled={sendingMessage}
                    />
                    <button
                      type="submit"
                      disabled={sendingMessage || !nuovoMessaggio.trim()}
                      className="px-3 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Azioni richiedente: Carica Documenti */}
            {isRichiedente && atto.studioAssegnato && atto.status === 'assegnata' && (
              <button
                onClick={() => navigate(`/area-riservata/svincolo-atti/upload/${atto._id}`)}
                className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Carica Documenti
              </button>
            )}
            {isRichiedente && atto.studioAssegnato && (atto.status === 'documenti_caricati' || atto.status === 'in_attesa_pagamento' || atto.status === 'completata') && (
              <div className="space-y-2">
                <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Documenti caricati con successo
                </p>
                {atto.status !== 'completata' && (
                  <button
                    onClick={() => navigate(`/area-riservata/svincolo-atti/upload/${atto._id}`)}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Aggiungi altri documenti
                  </button>
                )}
              </div>
            )}

            {/* Documenti spostati nella sidebar destra */}

            {/* Messaggi di Stato e Azioni */}

            {/* Studio Selezionato - In Attesa Documenti */}
            {isStudioAssegnato && atto.status === 'assegnata' && (!atto.documenti || atto.documenti.length === 0) && (
              <div className="bg-blue-50 dark:bg-blue-900/15 border border-blue-300 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">In Attesa dei Documenti</h3>
                    <p className="text-xs text-blue-800 dark:text-blue-200">
                      Sei stato selezionato! Attendi che il richiedente carichi i documenti. Ti avviseremo via email.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Studio Selezionato - Documenti Ricevuti - Form IBAN (se bonifico) */}
            {isStudioAssegnato && atto.status === 'documenti_caricati' && atto.modalitaPagamento === 'bonifico' && !atto.ibanStudioSelezionato && (
              <div className="bg-orange-50 dark:bg-orange-900/15 border border-orange-400 dark:border-orange-700 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-0.5">Inserisci il tuo IBAN</h3>
                    <p className="text-xs text-orange-800 dark:text-orange-200">Documenti caricati. Inserisci il tuo IBAN per ricevere il pagamento.</p>
                  </div>
                </div>
                <div className="space-y-2.5">
                  <input
                    type="text"
                    value={iban}
                    onChange={(e) => setIban(e.target.value.toUpperCase())}
                    placeholder="IT00X0000000000000000000000"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white font-mono"
                    maxLength={34}
                  />
                  <button
                    onClick={handleInserisciIban}
                    className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Euro className="w-4 h-4" />
                    Invia IBAN
                  </button>
                </div>
              </div>
            )}

            {/* Studio Selezionato - IBAN Inviato - In Attesa Ricevuta */}
            {isStudioAssegnato && atto.status === 'in_attesa_pagamento' && atto.modalitaPagamento === 'bonifico' && !atto.ricevutaBonifico?.path && (
              <div className="bg-yellow-50 dark:bg-yellow-900/15 border border-yellow-400 dark:border-yellow-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1">In Attesa del Pagamento</h3>
                    <p className="text-xs text-yellow-800 dark:text-yellow-200 mb-2">
                      IBAN inviato. Attendi il bonifico e la ricevuta dal richiedente.
                    </p>
                    <p className="font-mono text-xs bg-white dark:bg-gray-800 rounded px-2 py-1 border border-yellow-300 dark:border-yellow-700 text-gray-900 dark:text-white inline-block">
                      {atto.ibanStudioSelezionato}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Richiedente - Carica Ricevuta Bonifico */}
            {isRichiedente && atto.status === 'in_attesa_pagamento' && atto.modalitaPagamento === 'bonifico' && atto.ibanStudioSelezionato && !atto.ricevutaBonifico?.path && (
              <div id="pagamento" className="bg-purple-50 dark:bg-purple-900/15 border border-purple-400 dark:border-purple-700 rounded-lg p-4 scroll-mt-4">
                <div className="flex items-start gap-3 mb-3">
                  <AlertCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-0.5">Effettua il Pagamento</h3>
                    <p className="text-xs text-purple-800 dark:text-purple-200">Effettua il bonifico e carica la ricevuta.</p>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-200 dark:border-purple-800 mb-3 grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">IBAN Studio</p>
                    <p className="font-mono text-sm font-semibold text-purple-600 dark:text-purple-400">{atto.ibanStudioSelezionato}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Importo</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatCurrency(atto.preventivi?.find((p: any) => p.accettato)?.costoAtto || 0)}
                    </p>
                  </div>
                </div>

                <div className="space-y-2.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ricevuta Bonifico (PDF, JPG, PNG) *</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setRicevutaBonifico(e.target.files?.[0] || null)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <button
                    onClick={handleCaricaRicevuta}
                    disabled={!ricevutaBonifico}
                    className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-4 h-4" />
                    Carica Ricevuta
                  </button>
                </div>
              </div>
            )}

            {/* Studio Selezionato - Ricevuta Caricata - Conferma Completamento */}
            {isStudioAssegnato && (atto.status === 'in_attesa_pagamento' || atto.status === 'documenti_caricati') && (
              (atto.modalitaPagamento === 'venditore') ||
              (atto.modalitaPagamento === 'bonifico' && atto.ricevutaBonifico?.path)
            ) && !atto.pagamentoConfermato && (
              <div id="gestione" className="bg-green-50 dark:bg-green-900/15 border border-green-400 dark:border-green-700 rounded-lg p-4 scroll-mt-4">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-0.5">Conferma Completamento</h3>
                    <p className="text-xs text-green-800 dark:text-green-200">
                      {atto.modalitaPagamento === 'bonifico'
                        ? 'Verifica il pagamento e conferma il completamento.'
                        : 'Conferma quando hai completato la pratica e ricevuto il pagamento.'}
                    </p>
                  </div>
                </div>

                {atto.ricevutaBonifico?.path && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-300 dark:border-green-700 mb-3">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ricevuta Bonifico:</p>
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.get(
                            `${API_BASE_URL}/api/atti-svincolo/${id}/download-ricevuta`,
                            {
                              headers: getAuthHeader(),
                              responseType: 'blob'
                            }
                          );

                          const blob = response.data;
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = atto.ricevutaBonifico?.originalName || 'ricevuta-bonifico.pdf';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);

                          toast.success('Ricevuta scaricata con successo');
                        } catch (error: any) {
                          console.error('Errore download ricevuta:', error);
                          toast.error(error.response?.data?.message || 'Errore durante il download');
                        }
                      }}
                      className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      {atto.ricevutaBonifico.originalName}
                    </button>
                  </div>
                )}

                <div className="space-y-2.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Atto di Vendita (PDF) *</label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setAttoVenditaPDF(e.target.files?.[0] || null)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                    />
                    {attoVenditaPDF && (
                      <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> {attoVenditaPDF.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">N. Cartella PRA *</label>
                    <input
                      type="text"
                      value={numeroCartellaPRA}
                      onChange={(e) => setNumeroCartellaPRA(e.target.value.toUpperCase())}
                      placeholder="Es: 12345/2024"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white font-mono"
                    />
                  </div>
                  <button
                    onClick={handleConfermaCompletamento}
                    disabled={!attoVenditaPDF || !numeroCartellaPRA}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Conferma Completamento
                  </button>
                </div>
              </div>
            )}

            {/* Pratica Completata */}
            {atto.status === 'completata' && (
              <div className="bg-green-50 dark:bg-green-900/15 border border-green-400 dark:border-green-700 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-0.5">
                      Pratica Completata
                    </h3>
                    <p className="text-xs text-green-800 dark:text-green-200">
                      {isRichiedente
                        ? 'La tua pratica è stata completata. Scarica l\'atto di vendita qui sotto.'
                        : 'La pratica è stata completata con successo.'}
                      {atto.dataCompletamento && ` — ${formatDate(atto.dataCompletamento)}`}
                    </p>
                  </div>
                </div>

                {(isRichiedente || isStudioAssegnato) && (atto.attoVenditaPDF?.path || atto.numeroCartellaPRA) && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3.5 border border-green-300 dark:border-green-700 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                      Documenti Finali
                    </h4>

                    {atto.attoVenditaPDF?.path && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                        <p className="text-xs text-blue-700 dark:text-blue-300 mb-1.5 font-semibold uppercase tracking-wide">
                          Atto di Vendita
                        </p>
                        <button
                          onClick={async () => {
                            try {
                              const authHeader = getAuthHeader() as { Authorization?: string };
                              const token = authHeader.Authorization?.replace('Bearer ', '') || '';

                              const response = await axios.get(
                                `${API_BASE_URL}/api/atti-svincolo/${id}/download-atto`,
                                {
                                  headers: { Authorization: `Bearer ${token}` },
                                  responseType: 'blob'
                                }
                              );

                              // Crea un URL temporaneo per il blob e scarica il file
                              const blob = new Blob([response.data], { type: 'application/pdf' });
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = atto.attoVenditaPDF?.originalName || 'atto-vendita.pdf';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);

                              toast.success('Atto di vendita scaricato con successo');
                            } catch (error: any) {
                              console.error('Errore download atto:', error);
                              toast.error(error.response?.data?.message || 'Errore durante il download dell\'atto');
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-sm"
                        >
                          <FileText className="w-5 h-5" />
                          Scarica PDF
                        </button>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                          {atto.attoVenditaPDF.originalName}
                        </p>
                      </div>
                    )}

                    {atto.numeroCartellaPRA && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 border border-amber-300 dark:border-amber-700">
                        <p className="text-xs text-amber-800 dark:text-amber-300 mb-0.5 font-semibold uppercase tracking-wide">N. Cartella PRA</p>
                        <p className="font-mono text-sm font-bold text-amber-900 dark:text-amber-100">{atto.numeroCartellaPRA}</p>
                      </div>
                    )}

                    {isRichiedente && (
                      <p className="text-xs text-green-700 dark:text-green-300 flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 flex-shrink-0" />
                        Conserva questi documenti. Puoi tornare qui per scaricarli.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-3">
            {/* Card unificata: Studi e Preventivi */}
            <div id="preventivi" className="bg-white dark:bg-gray-800 rounded-lg border-l-4 border-l-indigo-500 dark:border-l-indigo-400 border border-gray-200 dark:border-gray-700 p-4 shadow-sm scroll-mt-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2.5 flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                {atto.studioAssegnato ? 'Studio Assegnato' : `Studi Selezionati (${atto.studiSelezionati?.length || 0})`}
                {!atto.studioAssegnato && atto.preventivi && atto.preventivi.length > 0 && (
                  <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded-full ml-auto">
                    {atto.preventivi.length} preventiv{atto.preventivi.length === 1 ? 'o' : 'i'}
                  </span>
                )}
              </h2>

              <div className="space-y-2">
                {/* Studio assegnato in evidenza */}
                {atto.studioAssegnato && (() => {
                  const studioAss = atto.studioAssegnato;
                  const prevAccettato = atto.preventivi?.find((p: any) => p.accettato || studioAss._id === p.studio?._id);
                  const sedeSnapAss = (atto.sediSelezionate || []).find(
                    (s: any) => (s.studio?._id || s.studio)?.toString() === studioAss._id?.toString()
                  );
                  const sedeLabelAss = sedeSnapAss && sedeSnapAss.indirizzoSnapshot
                    ? `${sedeSnapAss.nomeSnapshot || 'Sede'} — ${sedeSnapAss.indirizzoSnapshot}`
                    : '';
                  return (
                    <div className="p-3 rounded-lg border-2 border-green-400 dark:border-green-700 bg-green-50 dark:bg-green-900/15">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <span className="text-xs font-semibold text-green-700 dark:text-green-300">Studio Assegnato</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {studioAss.businessName || studioAss.associateId?.nominativo}
                      </p>
                      {sedeLabelAss && (
                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">📍 {sedeLabelAss}</p>
                      )}
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        {[
                          // Senza snapshot sede (studio mono-sede) mostra l'indirizzo dell'associato
                          !sedeLabelAss && [studioAss.associateId?.indirizzo, studioAss.associateId?.civico].filter(Boolean).join(' '),
                          studioAss.associateId?.citta,
                          studioAss.associateId?.provincia
                        ].filter(Boolean).join(', ')}
                        {studioAss.associateId?.email && ` — ${studioAss.associateId.email}`}
                      </p>
                      {prevAccettato && (
                        <p className="text-sm font-bold text-green-600 dark:text-green-400 mt-1.5">
                          {formatCurrency(prevAccettato.costoAtto || prevAccettato.importo || 0)}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Lista studi (pre-assegnazione o studi non assegnati) */}
                {atto.studiSelezionati?.map((studio: any) => {
                  const isAssegnato = atto.studioAssegnato?._id === studio._id;
                  if (isAssegnato) return null; // gia mostrato sopra

                  const sedeSnap = (atto.sediSelezionate || []).find(
                    (s: any) => (s.studio?._id || s.studio)?.toString() === studio._id?.toString()
                  );
                  const sedeLabel = sedeSnap && sedeSnap.indirizzoSnapshot
                    ? `${sedeSnap.nomeSnapshot || 'Sede'} — ${sedeSnap.indirizzoSnapshot}`
                    : '';

                  const preventivoStudio = atto.preventivi?.find(
                    (p: any) => (p.studio?._id || p.studio) === studio._id
                  );
                  const haRifiutato = atto.rifiuti?.some((r: any) => (r.studio?._id || r.studio) === studio._id);
                  const haPreventivo = preventivoStudio && !haRifiutato;

                  // Dopo assegnazione, mostra solo un riepilogo compatto degli altri
                  if (atto.studioAssegnato) {
                    return (
                      <div key={studio._id} className="text-xs p-2 rounded bg-gray-50 dark:bg-gray-700/30 flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          {studio.businessName || studio.associateId?.nominativo || 'Studio'}
                          {sedeLabel && (
                            <span className="block text-purple-600 dark:text-purple-400">📍 {sedeLabel}</span>
                          )}
                        </span>
                        {haPreventivo && (
                          <span className="text-gray-400 dark:text-gray-500">{formatCurrency(preventivoStudio.costoAtto || preventivoStudio.importo || 0)}</span>
                        )}
                        {haRifiutato && (
                          <span className="text-red-400 dark:text-red-500 flex items-center gap-0.5"><XCircle className="w-3 h-3" /> Rifiutato</span>
                        )}
                      </div>
                    );
                  }

                  // Pre-assegnazione: card completa con preventivo e azione
                  return (
                    <div
                      key={studio._id}
                      className={`p-2.5 rounded-lg border ${
                        haPreventivo
                          ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10'
                          : haRifiutato
                          ? 'border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/5'
                          : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                            {studio.businessName || studio.associateId?.nominativo || 'Studio'}
                          </p>
                          {(studio.associateId?.indirizzo || studio.associateId?.citta || studio.associateId?.provincia) && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {[
                                // Senza snapshot sede (studio mono-sede) mostra l'indirizzo dell'associato
                                !sedeLabel && [studio.associateId?.indirizzo, studio.associateId?.civico].filter(Boolean).join(' '),
                                studio.associateId?.citta,
                                studio.associateId?.provincia
                              ].filter(Boolean).join(', ')}
                            </p>
                          )}
                          {sedeLabel && (
                            <p className="text-xs text-purple-600 dark:text-purple-400">📍 {sedeLabel}</p>
                          )}
                        </div>
                        {haPreventivo && (
                          <span className="text-sm font-bold text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2">
                            {formatCurrency(preventivoStudio.costoAtto || preventivoStudio.importo || 0)}
                          </span>
                        )}
                        {haRifiutato && (
                          <span className="text-xs text-red-500 dark:text-red-400 flex items-center gap-0.5 flex-shrink-0 ml-2">
                            <XCircle className="w-3 h-3" /> Rifiutato
                          </span>
                        )}
                        {!haPreventivo && !haRifiutato && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-0.5 flex-shrink-0 ml-2">
                            <Clock className="w-3 h-3" /> In attesa
                          </span>
                        )}
                      </div>
                      {haPreventivo && preventivoStudio.note && (
                        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 italic">{preventivoStudio.note}</p>
                      )}
                      {/* Bottone accetta preventivo (solo richiedente, pre-assegnazione) */}
                      {isRichiedente && haPreventivo && !atto.studioAssegnato && (
                        <button
                          onClick={() => handleAccettaPreventivo(preventivoStudio._id)}
                          className="mt-2 w-full px-2.5 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                        >
                          <CheckCircle className="w-3 h-3" />
                          Accetta
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Form Preventivo (solo per studi selezionati) */}
            {isStudioSelezionato && !isRichiedente && !mioPreventivoEsistente && !atto.studioAssegnato && (
              <div id="preventivo" className="bg-orange-50 dark:bg-orange-900/15 rounded-lg border border-orange-400 dark:border-orange-700 p-4 scroll-mt-4">
                {!showRifiutoForm ? (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      <h2 className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                        Invia il tuo Preventivo
                      </h2>
                    </div>
                    <div className="space-y-2.5">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Importo (&euro;)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={preventivo.importo}
                          onChange={(e) => setPreventivo({ ...preventivo, importo: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="150.00"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Note (opzionale)</label>
                        <textarea
                          value={preventivo.note}
                          onChange={(e) => setPreventivo({ ...preventivo, note: e.target.value })}
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Note aggiuntive..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleInviaPreventivo}
                          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Send className="w-3.5 h-3.5" />
                          Invia
                        </button>
                        <button
                          onClick={() => setShowRifiutoForm(true)}
                          className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Rifiuta
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2.5">Rifiuta Richiesta</h2>
                    <div className="space-y-2.5">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Motivazione *</label>
                        <textarea
                          value={motivazioneRifiuto}
                          onChange={(e) => setMotivazioneRifiuto(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Motivo del rifiuto..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowRifiutoForm(false); setMotivazioneRifiuto(''); }}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={handleRifiutaRichiesta}
                          className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors flex items-center justify-center gap-1.5"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Conferma
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Preventivo già inviato o Studio Selezionato */}
            {mioPreventivoEsistente && (
              <div className={`rounded-lg p-4 ${
                isStudioAssegnato
                  ? 'bg-blue-50 dark:bg-blue-900/15 border border-blue-300 dark:border-blue-700'
                  : 'bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-800'
              }`}>
                {isStudioAssegnato ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <h2 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Studio Selezionato</h2>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Importo accettato</p>
                      <p className="text-base font-bold text-blue-600 dark:text-blue-400">
                        {formatCurrency(mioPreventivoEsistente.costoAtto || mioPreventivoEsistente.importo || 0)}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-sm font-semibold text-green-900 dark:text-green-100">Preventivo Inviato</h2>
                        <p className="text-xs text-green-700 dark:text-green-300">In attesa di risposta</p>
                      </div>
                      <p className="text-base font-bold text-green-900 dark:text-green-100">
                        {formatCurrency(mioPreventivoEsistente.costoAtto || mioPreventivoEsistente.importo || 0)}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Documenti (richiedente o studio assegnato) */}
            {(isRichiedente || isStudioAssegnato) && atto.documenti && atto.documenti.length > 0 && (
              <DocumentiVisualizzazione
                attoId={atto._id}
                documenti={atto.documenti}
                isRichiedente={isRichiedente}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal Conferma Cancellazione */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Conferma Cancellazione</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Sei sicuro di voler cancellare questa richiesta di svincolo?
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Verra inviata una email a tutti gli studi selezionati ({atto.studiSelezionati?.length || 0}) per informarli della cancellazione.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Motivo della cancellazione (opzionale)
                </label>
                <textarea
                  value={motivoCancellazione}
                  onChange={(e) => setMotivoCancellazione(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white resize-none"
                  placeholder="Es: Pratica non piu necessaria..."
                />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">Questa azione non puo essere annullata.</p>
            </div>
            <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex gap-2 justify-end">
              <button
                onClick={() => { setShowCancelModal(false); setMotivoCancellazione(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleCancellaRichiesta}
                className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SvincoloAttiDettaglioPage;

